"use client";

import { z } from "zod/v4";
import { ApiError } from "../admin-api/internal";
import { getClientToken, notifySessionExpired, refreshClientToken } from "../auth";
import { CLUB_API_URL, fetchApi, fetchVoid } from "../club-api/internal";
import {
	type VenueId,
	type VenuePhoto,
	type VenuePhotoId,
	venuePhotoSchema,
} from "../schemas/venue";

/** Upload progress as the fraction (0..1) of bytes sent. */
export type UploadProgress = (fraction: number) => void;

const put = (body: unknown): RequestInit => ({ body: JSON.stringify(body), method: "PUT" });

interface RawAnswer {
	status: number;
	text: string;
}

/** One multipart POST over XHR — fetch cannot report upload progress. */
function sendForm(
	url: string,
	form: FormData,
	token: string | null,
	onProgress?: UploadProgress,
): Promise<RawAnswer> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("POST", url);
		if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) onProgress?.(event.loaded / event.total);
		};
		xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText });
		xhr.onerror = () => reject(new TypeError("photo upload failed to reach the server"));
		xhr.send(form);
	});
}

async function upload(
	venueId: VenueId,
	file: Blob,
	onProgress?: UploadProgress,
): Promise<VenuePhoto> {
	const url = `${CLUB_API_URL}/venues/${venueId}/photos`;
	const form = new FormData();
	form.append("file", file);
	let answer = await sendForm(url, form, getClientToken(), onProgress);
	if (answer.status === 401) {
		const fresh = await refreshClientToken();
		if (fresh) answer = await sendForm(url, form, fresh, onProgress);
	}
	if (answer.status === 401) notifySessionExpired();
	if (answer.status < 200 || answer.status >= 300) {
		throw new ApiError(answer.status, answer.text);
	}
	return venuePhotoSchema.parse(JSON.parse(answer.text));
}

export const venuePhotosApi = {
	list: (venueId: VenueId) => fetchApi(`/venues/${venueId}/photos`, z.array(venuePhotoSchema)),
	remove: (venueId: VenueId, photoId: VenuePhotoId) =>
		fetchVoid(`/venues/${venueId}/photos/${photoId}`, { method: "DELETE" }),
	/** Every photo once, first = cover; a list built from a stale view answers 409. */
	reorder: (venueId: VenueId, photoIds: readonly VenuePhotoId[]) =>
		fetchApi(`/venues/${venueId}/photos/order`, z.array(venuePhotoSchema), put({ photoIds })),
	/** `null` clears the caption. */
	updateCaption: (venueId: VenueId, photoId: VenuePhotoId, caption: string | null) =>
		fetchApi(`/venues/${venueId}/photos/${photoId}`, venuePhotoSchema, put({ caption })),
	upload,
};
